import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { FileHelperService, NotifyService } from '@app/services';
import { redactReportsToIfNotInArray } from '@employees/functions/org-chart';
import { ChartNodeInterface as ChartNode } from '@employees/interfaces/chart-node.interface';
import { TreeNode } from '@employees/interfaces/tree-node.interface';
import { Employee } from '@models/employee/employee.model';
import * as Sentry from '@sentry/angular';
import * as d3 from 'd3';
import { HierarchyNode } from 'd3-hierarchy';
import { OrgChart } from 'd3-org-chart';

@Component({
    selector: 'app-org-chart-v2',
    templateUrl: './org-chart-v2.template.html',
    styleUrls: ['./org-chart-v2.style.scss'],
})
export class OrgChartV2Component implements OnChanges {
    @ViewChild('chartContainer') chartContainer: ElementRef;
    @Output() onEmployeeClick = new EventEmitter<Employee>();
    @Input() nodes: TreeNode[] = [];
    @Input() showPhotos = true;
    @Input() showNames = true;
    @Input() showDepartmentColours = true;
    @Input() showPositions = true;
    chart: OrgChart<TreeNode>;
    topNode: HierarchyNode<any>;

    /**
     * After 100 employees, only show the top 3 lines of the chart
     */
    readonly LARGE_ORG_SIZE = 100;
    readonly LARGE_ORG_DEFAULT_DEPTH = 3;

    readonly NODE_WIDTH = 200;
    readonly VIEW_HEADER_HEIGHT = 230;
    readonly BACKGROUND_COLOUR = 'var(--grey-100)';

    constructor(
        private fileHelper: FileHelperService,
        private notify: NotifyService
    ) {}

    ngAfterViewInit(): void {
        if (!this.chart) {
            this.chart = new OrgChart();
        }
        this.drawChart();
    }

    ngOnChanges(): void {
        this.drawChart();
    }

    render(): void {
        this.chart.render();
    }

    fullscreen(): void {
        this.chart.fullscreen();
    }

    zoomIn(): void {
        this.chart.zoomIn();
    }

    zoomOut(): void {
        this.chart.zoomOut();
    }

    expandAll(): void {
        this.chart.expandAll().fit();
    }

    download(): void {
        this.chart.fit();
        this.chart.exportImg({ full: true, scale: 10 });
    }

    /**
     * Can't use the default collapse method, as this will collapse the root
     */
    collapseAll(renderAndFit = true): void {
        const { allNodes } = this.chart.getChartState();
        allNodes.forEach((d) => {
            if (d?.parent?.id !== 'root') {
                d.data._expanded = false;
            }
        });
        this.chart.expandLevel(0);
        if (renderAndFit) {
            this.chart.render();
            this.chart.fit();
        }
    }

    drawChart(): void {
        if (!this.nodes || !this.chart) {
            return;
        }

        const data = redactReportsToIfNotInArray(this.nodes);

        try {
            this.chart
                .container(this.chartContainer.nativeElement)
                .data(data)
                .svgHeight(window.innerHeight - this.VIEW_HEADER_HEIGHT)
                .backgroundColor(this.BACKGROUND_COLOUR)
                // .initialZoom(0.2)
                .compact(false)
                .onNodeClick((d) => this.onNodeClick(d))
                .linkUpdate(this.renderLine)
                .nodeWidth(() => this.NODE_WIDTH)
                .nodeContent((d) => this.renderHtml(d as ChartNode))
                .nodeHeight((d) => this.calculateHeight(d))
                .childrenMargin((d) => this.calculateRowMargin(d))
                .render();
        } catch (e) {
            if (e instanceof Error && e.message === 'cycle') {
                this.notify.error('employees.organizationChart.cyclicalReportError');
            }
            Sentry.captureException(e);
        }

        /**
         * Depending on company size, open all nodes or only a certain depth on the tree
         */
        this.expandToOrg();

        this.chart.render().fit();

        /**
         * Always hide the first element, this is a root node we use to group all data under
         */
        this.hideRootNode();

        /**
         * Detect our primary node, based on who has the most child nodes
         */
        this.topNode = this.chart
            .getChartState()
            .root?.children?.reduce((prev, current) =>
                prev.data._totalSubordinates > current.data._totalSubordinates ? prev : current
            );
    }

    /**
     * Based on org size, expand all or only expand top of tree (avoids large companies loading tiny on screen)
     */
    expandToOrg(): void {
        if (!this.chart.data()?.length) {
            return;
        }

        if (this.chart.data()?.length > this.LARGE_ORG_SIZE) {
            this.collapseAll(false);
            this.expandToDepth(this.chart.getChartState().root, this.LARGE_ORG_DEFAULT_DEPTH);
            return;
        }
        this.chart.expandAll();
    }

    /**
     * Expand the chart from a root node down to a given depth
     */
    expandToDepth(node: ChartNode, depth: number): void {
        if (depth === 0) {
            return;
        }
        const next = depth - 1;

        // expand hidden children
        if (node._children?.length) {
            node._children.forEach((child) => {
                child.data._expanded = true;
                this.expandToDepth(child, next);
            });
        }

        if (node.children?.length) {
            // expand visible children (and grand chidren)
            node.children.forEach((child) => {
                child.data._expanded = true;
                this.expandToDepth(child, next);
            });
        }
    }

    renderHtml({ data: data }: ChartNode): string {
        if (data.id === 'root') {
            return '';
        }

        // avatar color
        const departmentColor =
            this.showDepartmentColours && data.departmentColour ? data.departmentColour : 'var(--grey-900)';

        let html = `
                <div class="org-chart-employee" data-id="${data.id}">`;
        /**
         * Showing photos, or initials if no photos
         */
        if (this.showPhotos) {
            /**
             * If employee has an avatar, directly render inline in chart
             */
            let image = '';
            let placeholder = '';
            if (data?.avatarId) {
                const avatarUrl = this.fileHelper.serveWithToken(data?.avatarId);
                image = `<img
                    class='avatar-image'
                    src='${avatarUrl}'
                    loading='lazy'
                >`;
            } else {
                /**
                 * No profile photo, use initials instead
                 */
                placeholder = `<div class="avatar-placeholder">
                    ${data.initials}
                </div>`;
            }

            html += `<div class="avatar" style='border-color: ${departmentColor}'>${image} ${placeholder}</div>`;
        }
        if (this.showNames) {
            html += `<div class="name">${data.name}</div>`;
        }

        if (this.showPositions) {
            html += `<div class="position"
                        >${data?.position ?? ''}</div>`;
        }
        html += `</div>`;

        html += `<style>
            .org-chart-employee {
                padding: 20px 10px 20px 10px;
                border: 1px solid grey;
                border-radius: 8px;
                background: var(--card-background-color);
                text-align: center;
            }
            .org-chart-employee .avatar {
                border: 1px solid;
                width:50px;
                height:50px;
                border-radius: 50%;
                margin: auto;
                overflow: hidden;
            }
            .org-chart-employee .avatar .avatar-image {
                width: 50px;
                height: 50px;
                background: var(--grey-400);
                color: var(--grey-800);
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .org-chart-employee .avatar .avatar-placeholder {
                background: var(--grey-400);
                color: var(--grey-800);
                display: flex;
                align-items: center;
                justify-content: center;
                height: 50px;
                width: 50px;
                border-radius: 50%;
                margin:0 auto 5px auto;
            }
            .org-chart-employee .position {
                font-size: 12px;
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
            .org-chart-employee .name {
                font-size: 14px;
            }
            .org-chart-employee:hover {
                border: 1px solid var(--primary-600);
                box-shadow: 0 7px 14px color-mix(in hsl, var(--grey-900), transparent 92%), 0 3px 6px color-mix(in hsl, var(--black), transparent 95%);
            }
            .org-chart-employee:hover .name, .org-chart-employee:hover .position {
                color: var(--primary-600);
            }
        </style>`;
        return html;
    }

    private findNode(nodeId): HierarchyNode<TreeNode> {
        const state = this.chart.getChartState();
        return state.allNodes.filter((d) => state.nodeId(d.data) === nodeId)[0];
    }

    private calculateHeight(node): number {
        if (node.id === 'root') {
            return 0;
        }
        let base = 45;
        if (this.showPhotos) {
            base += 55;
        }
        if (this.showNames) {
            base += 20;
        }
        if (this.showPositions) {
            base += 15;
        }
        return base;
    }

    private renderLine(node): void {
        /**
         * If node inherits from the hidden root node, we do not show the line
         */
        if (node?.data?.parentId === 'root') {
            return;
        }
        d3.select(this).attr('stroke', 'var(--grey-400, hsl(0, 0%, 33%))').attr('stroke-width', 2);
    }

    private calculateRowMargin(node): number {
        if (node.id === 'root') {
            return 0;
        }
        return 60;
    }

    private onNodeClick(nodeId): void {
        this.onEmployeeClick.emit(this.findNode(nodeId)?.data?.employee);
    }

    private hideRootNode(): void {
        const rootElement = document.querySelector('g.node') as HTMLElement;
        rootElement.style.display = 'none';
    }
}
